import type { CartDetailsItemResponse, CartDetailsResponse } from '@/types/carts-transactions-api';
import type { ProductDTO } from '@/types/products';

import { convertOctoPrice, pushEventEcommerce } from '@/utils/utils';

type GA4Item = {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  currency: string;
  item_variant?: string;
  item_brand?: string;
  discount?: number;
};

type GA4EcommercePayload = {
  currency?: string;
  value?: number;
  transaction_id?: string;
  items: GA4Item[];
};

function buildGA4ItemFromProduct(product: ProductDTO): GA4Item {
  const discount = product.originalPrice > product.priceFrom
    ? product.originalPrice - product.priceFrom
    : undefined;

  return {
    item_id: product.id,
    item_name: product.name,
    price: product.priceFrom,
    quantity: 1,
    currency: product.currency,
    item_brand: product.source,
    discount,
  };
}

function buildGA4ItemFromCartItem(
  item: CartDetailsItemResponse,
  currency: string,
  currencyPrecision: number,
): GA4Item[] {
  return item.units.map((unit) => {
    const price = convertOctoPrice(unit.retailPrice, currencyPrecision);
    const originalPrice = unit.originalPrice
      ? convertOctoPrice(unit.originalPrice, currencyPrecision)
      : undefined;
    const discount = originalPrice && originalPrice > price
      ? originalPrice - price
      : undefined;

    return {
      item_id: item.productId,
      item_name: item.bookingDetails?.productName ?? '',
      price,
      quantity: unit.quantity,
      currency,
      item_variant: item.optionId,
      discount,
    };
  });
}

function buildGA4ItemsFromCart(cartDetails: CartDetailsResponse): GA4Item[] {
  return cartDetails.items.flatMap(item =>
    buildGA4ItemFromCartItem(item, cartDetails.currency, cartDetails.currencyPrecision),
  );
}

function computeCartValue(cartDetails: CartDetailsResponse): number {
  return convertOctoPrice(cartDetails.totalPrice, cartDetails.currencyPrecision);
}

function pushGA4EcommerceEvent(eventName: string, ecommerce: GA4EcommercePayload): void {
  pushEventEcommerce({ event: eventName, ecommerce: null });
  pushEventEcommerce({ event: eventName, ecommerce });
}

export type { GA4EcommercePayload, GA4Item };
export {
  buildGA4ItemFromCartItem,
  buildGA4ItemFromProduct,
  buildGA4ItemsFromCart,
  computeCartValue,
  pushGA4EcommerceEvent,
};
